import { RefObject } from 'react';
import { Divider } from '@chakra-ui/react';
import { getEndpointWithParams } from '@/routes/config';
import { trioUITheme } from '@/styles/theme';
import { Job } from '@/types/api';
import { Paths } from '@/types/routes';
import * as Styles from './JobSearchList.styles';
import Loading from '../general/Loading';

export type JobSearchListProps = {
  jobs: Job[];
  isLoading: boolean;
  hasFetched: boolean;
  hasError: boolean;
  containerRef?: RefObject<HTMLDivElement>;
};

const JobSearchList = ({
  jobs,
  isLoading,
  hasError,
  hasFetched,
  containerRef,
}: JobSearchListProps): JSX.Element => {
  const doesNotHaveJobs = !jobs.length && hasFetched && !isLoading;
  const hasJobs = !!jobs.length && hasFetched && !isLoading;
  const thereWasError = hasError && !isLoading;

  const renderJobs = (job: Job, index: number): JSX.Element => {
    const jobLink = getEndpointWithParams(Paths.JOB_DETAILS, {
      jobId: job.id,
    });

    return (
      <Styles.ListItem data-testid={`job-result-item-${index}`} key={job.id}>
        <Styles.ListItemLink data-testid={`job-result-item-${index}-link`} to={jobLink}>
          <Styles.JobDetails>
            <Styles.FirstRow>
              <Styles.JobTitle>{job.title}</Styles.JobTitle>
              <Styles.CustomStatusTag
                status={job.status === 'Active' ? 'Open' : job.status}
                statusColor={trioUITheme.colors['purple-50']}
              />
            </Styles.FirstRow>

            <Styles.SecondRow>
              <Styles.Text>{job.account?.name}</Styles.Text>
              <Divider orientation="vertical" height={4} />
              <Styles.WhenToStart>{job.whenToStart}</Styles.WhenToStart>
            </Styles.SecondRow>
          </Styles.JobDetails>
        </Styles.ListItemLink>
      </Styles.ListItem>
    );
  };

  return (
    <div ref={containerRef} id="job-search-list" data-testid="job-search-list">
      {isLoading && (
        <Styles.Box data-testid="job-search-loading">
          <Styles.LoadingArea>
            <Loading size="16px" />
          </Styles.LoadingArea>
        </Styles.Box>
      )}

      {doesNotHaveJobs && (
        <Styles.Box data-testid="job-search-not-found">
          <Styles.NotFound>No jobs found.</Styles.NotFound>
        </Styles.Box>
      )}

      {thereWasError && (
        <Styles.Box data-testid="job-search-error">
          <Styles.ErrorMessage>There was an error. Please, try again.</Styles.ErrorMessage>
        </Styles.Box>
      )}

      {hasJobs && (
        <Styles.Box data-testid="job-search-results">
          <Styles.List>{jobs.map(renderJobs)}</Styles.List>
        </Styles.Box>
      )}
    </div>
  );
};

export default JobSearchList;
